import { BUILTINS } from './builtins';
import { FormulaRegistry, FunctionSpec, paramAt, ParamSpec } from './registry';
import { AstNode, FieldTypeResolver, FormulaTypeError, ValueType } from './types';

export { FormulaTypeError, type FieldType, type FieldTypeResolver } from './types';

/**
 * A node's type while checking: a value type, or 'unknown' for what nothing
 * pins down yet — a blank literal, a bound name without a value, or (in
 * lenient inference) a field the lookup does not know. Unknown unifies with
 * anything.
 */
export type InferredType = ValueType | 'unknown';

/**
 * The static rules, applied in one pass that also infers every node's type:
 *
 * - Arithmetic and unary sign take numbers; `=`/`<>` compare two values of
 *   one type; `<`/`<=`/`>`/`>=` do too, numbers, text or dates only.
 *   Comparisons yield TRUE/FALSE. CONCAT (`&`) is a function: any scalars in,
 *   text out.
 * - A registered function's expr params accept what their `type` says: one
 *   value type, anything ('any'), or the call's shared type ('same' — every
 *   'same' argument, a 'same' member and a LAMBDA body agree on one type,
 *   and a 'same' return yields it).
 * - Placement, as before: a list only where a param takes one, a member only
 *   inside a REDUCE over its list or in a member param of a call over that
 *   list, a KPI ref only where the KPI entry's own check allows it.
 *
 * Throws FormulaTypeError on the first violation — with the offending node,
 * so an editor can underline the expression, and the field index where a ref
 * is at fault. Returns the formula's type. Runs on the canonical (path-based)
 * tree; evaluate() mirrors the rules as #VALUE! so a stored formula that
 * predates a vocabulary change still degrades to a blank cell.
 */
export function typecheck(
  ast: AstNode,
  lookup: FieldTypeResolver,
  registry: FormulaRegistry = BUILTINS
): ValueType {
  const type = infer(ast, { lookup, nextField: 0, registry, strict: true }, [], new Map());
  return type === 'unknown' ? 'number' : type;
}

/**
 * The same inference without the rules: an unknown field, a misplaced list or
 * a mismatch never throws, the first type that is known wins, and a formula
 * nothing pins down is a number. For surfaces that only need the result type
 * of a formula the editor has already validated.
 */
export function inferType(
  ast: AstNode,
  lookup: FieldTypeResolver,
  registry: FormulaRegistry = BUILTINS
): ValueType {
  const type = infer(ast, { lookup, nextField: 0, registry, strict: false }, [], new Map());
  return type === 'unknown' ? 'number' : type;
}

interface CheckState {
  lookup: FieldTypeResolver;
  /** Index of the next field ref in source order — the walk visits them in that order. */
  nextField: number;
  registry: FormulaRegistry;
  strict: boolean;
}

/** LET names and REDUCE accumulators in scope, with the types of their values. */
type Names = ReadonlyMap<string, InferredType>;

/** How a type reads in a message. */
export function describeType(type: InferredType): string {
  switch (type) {
    case 'number':
      return 'a number';
    case 'string':
      return 'text';
    case 'boolean':
      return 'TRUE/FALSE';
    case 'date':
      return 'a date';
    default:
      return 'an unknown value';
  }
}

/** The one type two sides agree on, or undefined when they are two known, different types. */
function unify(a: InferredType, b: InferredType): InferredType | undefined {
  if (a === 'unknown') {
    return b;
  }
  if (b === 'unknown' || a === b) {
    return a;
  }
  return undefined;
}

/** In strict mode a violation throws; lenient inference just carries on with what it has. */
function fail(state: CheckState, error: FormulaTypeError): void {
  if (state.strict) {
    throw error;
  }
}

function infer(node: AstNode, state: CheckState, lists: string[], names: Names): InferredType {
  switch (node.kind) {
    case 'num':
      return 'number';
    case 'str':
      return 'string';
    case 'bool':
      return 'boolean';
    case 'param':
      return names.get(node.name) ?? 'unknown';
    case 'field':
      return checkField(node, state, lists);
    case 'unary': {
      const operand = infer(node.operand, state, lists, names);
      if (!unify(operand, 'number')) {
        fail(
          state,
          new FormulaTypeError(
            `"${node.op}" needs a number — this is ${describeType(operand)}`,
            '',
            -1,
            node.operand
          )
        );
      }
      return 'number';
    }
    case 'binary':
      return inferBinary(node, state, lists, names);
    case 'lambda':
      return infer(node.body, state, lists, names);
    case 'let': {
      // Names add nothing to the list scope: a value or the calculation may
      // read a member exactly where the enclosing REDUCE lets it.
      const scope = new Map(names);
      for (const { name, value } of node.bindings) {
        scope.set(name, infer(value, state, lists, scope));
      }
      return infer(node.body, state, lists, scope);
    }
    case 'call': {
      const spec = state.registry.get(node.fn);
      if (!spec) {
        node.args.forEach((arg) => infer(arg, state, lists, names));
        return 'unknown';
      }
      return inferCall(spec, node.args, state, lists, names);
    }
  }
}

const ORDERING_OPS = new Set(['<', '<=', '>', '>=']);
const ORDERABLE = new Set<InferredType>(['number', 'string', 'date', 'unknown']);

function inferBinary(
  node: Extract<AstNode, { kind: 'binary' }>,
  state: CheckState,
  lists: string[],
  names: Names
): InferredType {
  const left = infer(node.left, state, lists, names);
  const right = infer(node.right, state, lists, names);
  if (node.op === '=' || node.op === '<>' || ORDERING_OPS.has(node.op)) {
    if (!unify(left, right)) {
      fail(
        state,
        new FormulaTypeError(
          `"${node.op}" compares two values of the same type — this compares ${describeType(left)} with ${describeType(right)}`,
          '',
          -1,
          node
        )
      );
    } else if (ORDERING_OPS.has(node.op)) {
      const side = !ORDERABLE.has(left) ? node.left : !ORDERABLE.has(right) ? node.right : undefined;
      if (side) {
        fail(
          state,
          new FormulaTypeError(
            `"${node.op}" orders numbers, text or dates — this is ${describeType(side === node.left ? left : right)}`,
            '',
            -1,
            side
          )
        );
      }
    }
    return 'boolean';
  }
  for (const [side, type] of [
    [node.left, left],
    [node.right, right],
  ] as const) {
    if (!unify(type, 'number')) {
      fail(
        state,
        new FormulaTypeError(`"${node.op}" needs numbers — this is ${describeType(type)}`, '', -1, side)
      );
    }
  }
  return 'number';
}

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'];

/** "second argument of REDUCE", or "argument of SIZE" for a call with one param. */
function slotName(spec: FunctionSpec, index: number): string {
  return spec.params.length === 1
    ? `argument of ${spec.name}`
    : `${ORDINALS[index] ?? `${index + 1}th`} argument of ${spec.name}`;
}

/** What a fixed-type param says it takes, for a message. */
function describeParam(param: ParamSpec): string {
  return param.type && param.type !== 'any' && param.type !== 'same' ? describeType(param.type) : 'a value';
}

/**
 * Each argument checked by its param kind and type, in source order: a list
 * param sets the list that member and lambda params after it belong to; kpi
 * and token params are left to the entry's own check, which consumes their
 * field indexes itself; 'same' params (and a LAMBDA body) settle the call's
 * shared type together.
 */
function inferCall(
  spec: FunctionSpec,
  args: AstNode[],
  state: CheckState,
  lists: string[],
  names: Names
): InferredType {
  let list: { ref: string; label: string } | undefined;
  let shared: { type: InferredType; slot: string } = { type: 'unknown', slot: '' };

  const agree = (
    type: InferredType,
    node: AstNode,
    slot: string,
    position: string,
    fromLambda = false
  ): void => {
    if (spec.sameOf && type !== 'unknown' && !spec.sameOf.includes(type)) {
      fail(
        state,
        new FormulaTypeError(
          `${spec.name} takes ${spec.sameOf.map(describeType).join(' or ')} — the ${position} is ${describeType(type)}`,
          '',
          -1,
          node
        )
      );
      return;
    }
    const unified = unify(shared.type, type);
    if (unified === undefined) {
      fail(
        state,
        new FormulaTypeError(
          fromLambda
            ? `${spec.name}'s LAMBDA must return ${describeType(shared.type)}, like its ${shared.slot} — it returns ${describeType(type)}`
            : `${spec.name}'s ${shared.slot} and ${slot} must have the same type — ${describeType(shared.type)} and ${describeType(type)}`,
          '',
          -1,
          node
        )
      );
      return;
    }
    if (shared.type === 'unknown' && unified !== 'unknown') {
      shared = { type: unified, slot };
    }
  };

  args.forEach((arg, i) => {
    const param = paramAt(spec, i);
    const slot = param ? param.name : slotName(spec, i);
    switch (param?.kind) {
      case 'list':
        list = checkListArg(arg, state, slotName(spec, i), param.hint);
        return;
      case 'member': {
        if (!list) {
          infer(arg, state, lists, names);
          return;
        }
        const member = checkMemberArg(arg, list, state, slotName(spec, i));
        if (!member) {
          return;
        }
        if (param.numeric && member.type !== 'number') {
          fail(
            state,
            new FormulaTypeError(
              `"[${member.label}]" is a ${describeType(member.type)} field — the ${slotName(spec, i)} must be a numeric field of [${list.label}], e.g. [Transactions.Distributions]`,
              member.ref,
              member.at,
              arg
            )
          );
        }
        if (param.type === 'same') {
          agree(member.type, arg, slot, slotName(spec, i));
        }
        return;
      }
      case 'lambda': {
        const scope = new Map(names);
        if (arg.kind === 'lambda') {
          arg.params.forEach((name) => scope.set(name, shared.type));
        }
        const body = infer(arg, state, list ? [list.ref, ...lists] : lists, scope);
        if (param.type === 'same') {
          agree(body, arg.kind === 'lambda' ? arg.body : arg, slot, slotName(spec, i), true);
        }
        return;
      }
      case 'kpi':
      case 'token':
        return;
      default: {
        const type = infer(arg, state, lists, names);
        if (!param || param.type === undefined || param.type === 'any') {
          return;
        }
        if (param.type === 'same') {
          agree(type, arg, slot, slotName(spec, i));
          return;
        }
        if (!unify(type, param.type)) {
          fail(
            state,
            new FormulaTypeError(
              `The ${slotName(spec, i)} must be ${describeParam(param)} — this is ${describeType(type)}`,
              '',
              -1,
              arg
            )
          );
        }
      }
    }
  });
  if (state.strict) {
    spec.check?.(args, { lookup: state.lookup, nextField: () => state.nextField++ });
  }
  return spec.returns === 'same' ? shared.type : spec.returns;
}

/** Enforce a call argument that must be a list field; returns its ref and display label. */
function checkListArg(
  node: AstNode,
  state: CheckState,
  slot: string,
  hint?: string
): { ref: string; label: string } | undefined {
  const at = state.nextField++;
  const suffix = hint ? `. ${hint}` : '';
  if (node.kind !== 'field') {
    fail(
      state,
      new FormulaTypeError(
        `The ${slot} must be a list field, e.g. [Metrics.Transactions]${suffix}`,
        '',
        at,
        node
      )
    );
    return undefined;
  }
  const type = state.lookup(node.ref);
  if (!type) {
    fail(state, unknownField(node.ref, at, node));
    return { ref: node.ref, label: node.ref };
  }
  if (type.kind !== 'list') {
    fail(
      state,
      new FormulaTypeError(
        `"[${type.label ?? node.ref}]" is not a list — the ${slot} must be a list field, e.g. [Metrics.Transactions]${suffix}`,
        node.ref,
        at,
        node
      )
    );
  }
  return { ref: node.ref, label: type.label ?? node.ref };
}

/** A call argument that must be a member of the call's own list (INDEX's third, SUM's second). */
function checkMemberArg(
  node: AstNode,
  list: { ref: string; label: string },
  state: CheckState,
  slot: string
): { ref: string; label: string; type: InferredType; at: number } | undefined {
  const at = state.nextField++;
  if (node.kind !== 'field') {
    fail(
      state,
      new FormulaTypeError(
        `The ${slot} must be a field of [${list.label}], e.g. [Transactions.Distributions]`,
        '',
        at,
        node
      )
    );
    return undefined;
  }
  const type = state.lookup(node.ref);
  if (!type) {
    fail(state, unknownField(node.ref, at, node));
    return { ref: node.ref, label: node.ref, type: 'unknown', at };
  }
  if (type.kind === 'member' && type.list === list.ref) {
    return { ref: node.ref, label: type.label ?? node.ref, type: type.type, at };
  }
  const name = type.label ?? node.ref;
  fail(
    state,
    type.kind === 'other'
      ? new FormulaTypeError(
          `"[${name}]" is a ${type.type} field — formulas can reference number, text, TRUE/FALSE and date fields only`,
          node.ref,
          at,
          node
        )
      : new FormulaTypeError(
          `"[${name}]" is not a field of [${list.label}] — the ${slot} must be one of its fields, e.g. [Transactions.Distributions]`,
          node.ref,
          at,
          node
        )
  );
  // Lenient inference still learns the type a scalar lookup reports for the ref.
  return { ref: node.ref, label: name, type: type.kind === 'scalar' ? type.type : 'unknown', at };
}

/** A field read as a value: its scalar type, or (a member in scope) the member's. */
function checkField(
  node: Extract<AstNode, { kind: 'field' }>,
  state: CheckState,
  lists: string[]
): InferredType {
  const ref = node.ref;
  const at = state.nextField++;
  const type = state.lookup(ref);
  if (!type) {
    fail(state, unknownField(ref, at, node));
    return 'unknown';
  }
  const name = type.label ?? ref;
  switch (type.kind) {
    case 'scalar':
      return type.type;
    case 'list':
      fail(
        state,
        new FormulaTypeError(
          `"[${name}]" is a list — it can only be used with REDUCE, SUM, SIZE or INDEX, e.g. SIZE([${name}])`,
          ref,
          at,
          node
        )
      );
      return 'unknown';
    case 'member': {
      if (lists.includes(type.list)) {
        return type.type;
      }
      const listName = type.listLabel ?? type.list;
      fail(
        state,
        new FormulaTypeError(
          `"[${name}]" belongs to [${listName}] — use it inside REDUCE(…, [${listName}], LAMBDA(…)), as SUM([${listName}], [${name}]) or as INDEX([${listName}], index, [${name}])`,
          ref,
          at,
          node
        )
      );
      return type.type;
    }
    case 'kpi':
      fail(
        state,
        new FormulaTypeError(
          `"[${name}]" is a KPI — reference it with KPI, e.g. KPI([${name}], [Quarter], [Actual])`,
          ref,
          at,
          node
        )
      );
      return 'number';
    case 'other':
      fail(
        state,
        new FormulaTypeError(
          `"[${name}]" is a ${type.type} field — formulas can reference number, text, TRUE/FALSE and date fields only`,
          ref,
          at,
          node
        )
      );
      return 'unknown';
  }
}

function unknownField(ref: string, at: number, node: AstNode): FormulaTypeError {
  return new FormulaTypeError(`Unknown field "[${ref}]"`, ref, at, node);
}
